// Conversations client → gateway /api/v1/conversations/* (conversation-service).
// Same auth pattern as the rest of main-app: httpOnly session cookie
// (withCredentials) + attachCookieAuth (CSRF header on writes). See docs/
// conversations for the full contract. The tenant `conversations` entitlement gates
// every route (403 when absent) — the nav is entitlement-gated so users only reach
// this when enabled.
import axios from 'axios';
import { apiConfig } from '@/config/api.config';
import { attachCookieAuth } from '@/lib/session';
import { refreshAccessToken } from '@/lib/authRefresh';
import { fetchAttachmentBlob, fetchOfficePreview, type OfficePreviewResult, type PreviewDeps } from '../attachments';
import type { ConversationReceipts, MessageReceipts } from '../readState';
import type {
  Availability,
  AvailabilityInput,
  Conversation,
  ConversationSummary,
  DanaResult,
  MeetingProviders,
  Message,
  MessageGif,
  MessagePage,
  MyAvailability,
  NewPoll,
  ReactionResult,
  SpaceKind,
} from '../types';

const http = axios.create({ baseURL: apiConfig.baseURL, timeout: 30000, withCredentials: true });
http.interceptors.request.use(attachCookieAuth);

const base = '/api/v1/conversations';

export const conversationsApi = {
  /** The caller's active conversations (inbox). */
  async inbox(): Promise<ConversationSummary[]> {
    const { data } = await http.get<{ conversations: ConversationSummary[] }>(base);
    return data.conversations ?? [];
  },

  /** Resolve or create the ONE direct conversation with a recipient (idempotent). */
  async createDirect(recipientUserId: string, recipientEmployeeId?: string): Promise<Conversation> {
    const { data } = await http.post<Conversation>(base, {
      type: 'DIRECT',
      recipient_user_id: recipientUserId,
      recipient_employee_id: recipientEmployeeId,
    });
    return data;
  },

  async get(conversationId: string): Promise<Conversation> {
    const { data } = await http.get<Conversation>(`${base}/${conversationId}`);
    return data;
  },

  /** History scroll (before_seq, newest-first) or forward catch-up (after_seq,
   *  oldest-first). Pass one; after_seq wins if both are given. */
  async messages(
    conversationId: string,
    opts: { beforeSeq?: number; afterSeq?: number; limit?: number } = {},
  ): Promise<MessagePage> {
    const params: Record<string, number> = {};
    if (opts.beforeSeq) params.before_seq = opts.beforeSeq;
    if (opts.afterSeq) params.after_seq = opts.afterSeq;
    if (opts.limit) params.limit = opts.limit;
    const { data } = await http.get<MessagePage>(`${base}/${conversationId}/messages`, { params });
    return data;
  },

  /** Send a message. Pass a stable clientMessageId for optimistic + idempotent send
   *  (a retry returns the original, no duplicate). */
  async postMessage(
    conversationId: string,
    input: {
      bodyPlain: string;
      clientMessageId: string;
      replyToMessageId?: string;
      mentionUserIds?: string[];
      gif?: MessageGif;
      poll?: NewPoll;
    },
  ): Promise<Message> {
    const { data } = await http.post<Message>(`${base}/${conversationId}/messages`, {
      body_plain: input.bodyPlain,
      client_message_id: input.clientMessageId,
      reply_to_message_id: input.replyToMessageId,
      mention_user_ids: input.mentionUserIds,
      gif: input.gif,
      poll: input.poll,
    });
    return data;
  },

  /** Upload a file → posts a FILE message (and best-effort saves to My Documents). */
  async uploadAttachment(
    conversationId: string,
    file: File,
    opts: { caption?: string; clientMessageId?: string } = {},
  ): Promise<Message> {
    const form = new FormData();
    form.append('file', file);
    if (opts.caption) form.append('caption', opts.caption);
    if (opts.clientMessageId) form.append('client_message_id', opts.clientMessageId);
    const { data } = await http.post<Message>(`${base}/${conversationId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  /** File an uploaded file into a shared company Document Category (HR/admin only). */
  async fileToCategory(conversationId: string, messageId: string, category: string): Promise<void> {
    await http.post(`${base}/${conversationId}/messages/${messageId}/file-to-category`, { category });
  },

  /** Create a work assignment from this conversation → posts an assignment card. */
  async createAssignment(
    conversationId: string,
    input: { title: string; description?: string; priority?: string; dueAt?: string; assigneeEmployeeId?: string; assigneeName?: string },
  ): Promise<Message> {
    const { data } = await http.post<Message>(`${base}/${conversationId}/assignment`, {
      title: input.title,
      description: input.description,
      priority: input.priority,
      due_at: input.dueAt,
      assignee_employee_id: input.assigneeEmployeeId,
      assignee_name: input.assigneeName,
    });
    return data;
  },

  /** Share a song → posts a rich MUSIC card (server validates the provider link). */
  async shareMusic(
    conversationId: string,
    input: { title: string; artist?: string; url: string; artworkUrl?: string },
  ): Promise<Message> {
    const { data } = await http.post<Message>(`${base}/${conversationId}/music`, {
      title: input.title, artist: input.artist, url: input.url, artwork_url: input.artworkUrl,
    });
    return data;
  },

  /** Cancel a Circle meeting → updates the MEETING card (organizer/creator only). */
  async cancelMeeting(conversationId: string, meetingId: string): Promise<Message> {
    const { data } = await http.post<Message>(`${base}/${conversationId}/meeting/${meetingId}/cancel`, {});
    return data;
  },

  /** Reschedule a Circle meeting → updates the MEETING card (organizer/creator only). */
  async rescheduleMeeting(
    conversationId: string,
    meetingId: string,
    input: { startAt: string; endAt?: string; timezone?: string },
  ): Promise<Message> {
    const { data } = await http.patch<Message>(`${base}/${conversationId}/meeting/${meetingId}`, {
      start_at: input.startAt,
      end_at: input.endAt,
      timezone: input.timezone,
    });
    return data;
  },

  /** A FILE message's bytes, resolved fresh from the message id through the
   *  membership-gated endpoint. Renews an expired session once (401) and retries
   *  a network blip once — see ../attachments.ts. Never returns or caches a URL. */
  async fetchFile(conversationId: string, messageId: string): Promise<Blob> {
    return fetchAttachmentBlob({
      get: async () =>
        (await http.get<Blob>(`${base}/${conversationId}/messages/${messageId}/file`, { responseType: 'blob', timeout: 120000 })).data,
      refresh: refreshAccessToken,
    });
  },

  /** An Office file as a PDF (Office preview, spec §3.4): polls while the server
   *  prepares it. Same gate as /file; the PDF is never cached as a URL. */
  async fetchOfficePreview(
    conversationId: string,
    messageId: string,
    opts: Pick<PreviewDeps, 'onPreparing' | 'cancelled' | 'signal'> = {},
  ): Promise<OfficePreviewResult> {
    return fetchOfficePreview({
      // Each request gets only the time left of the 60 s promise, and is aborted
      // when the viewer closes (round 2).
      get: async ({ timeoutMs, signal }) => {
        const r = await http.get<Blob>(`${base}/${conversationId}/messages/${messageId}/preview`, {
          responseType: 'blob', timeout: timeoutMs, signal,
        });
        return { status: r.status, data: r.data, headers: r.headers as Record<string, unknown> };
      },
      refresh: refreshAccessToken,
      ...opts,
    });
  },

  /** Tenant meeting-provider discovery for a Circle (participant-gated). */
  async getMeetingProviders(conversationId: string): Promise<MeetingProviders> {
    const { data } = await http.get<MeetingProviders>(`${base}/${conversationId}/meeting/providers`);
    return data;
  },

  /** Schedule (or start) a meeting from a Circle → posts a MEETING card. Times are
   *  RFC3339 with the caller's offset; timezone is the IANA zone. */
  async scheduleMeeting(
    conversationId: string,
    input: {
      title: string;
      startAt: string;
      endAt?: string;
      description?: string;
      timezone?: string;
      provider?: string;
      attendeeUserIds?: string[];
      generateLink?: boolean;
    },
  ): Promise<Message> {
    const { data } = await http.post<Message>(`${base}/${conversationId}/meeting`, {
      title: input.title,
      start_at: input.startAt,
      end_at: input.endAt,
      description: input.description,
      timezone: input.timezone,
      provider: input.provider,
      attendee_user_ids: input.attendeeUserIds,
      generate_link: input.generateLink ?? true,
    });
    return data;
  },

  /** Edit the body of your own text message. Returns the updated message (edited_at set). */
  async editMessage(conversationId: string, messageId: string, bodyPlain: string): Promise<Message> {
    const { data } = await http.patch<Message>(`${base}/${conversationId}/messages/${messageId}`, {
      body_plain: bodyPlain,
    });
    return data;
  },

  /** Delete a message (sender, or conversation OWNER/ADMIN). Soft delete server-side. */
  async deleteMessage(conversationId: string, messageId: string): Promise<void> {
    await http.delete(`${base}/${conversationId}/messages/${messageId}`);
  },

  /** Set the caller's votes on a poll message. Returns the updated tally. */
  async votePoll(
    conversationId: string,
    messageId: string,
    optionIds: string[],
  ): Promise<{ counts: Record<string, number>; my_votes: string[] }> {
    const { data } = await http.put<{ counts: Record<string, number>; my_votes: string[] }>(
      `${base}/${conversationId}/messages/${messageId}/poll/vote`,
      { option_ids: optionIds },
    );
    return data;
  },

  /** Star/unstar a conversation for the caller (backs the Favorites filter). */
  async setFavorite(conversationId: string, favorite: boolean): Promise<void> {
    await http.put(`${base}/${conversationId}/favorite`, { favorite });
  },

  /** Hide/unhide a conversation for the caller only — the "Remove" row action.
   *  Caller-scoped and reversible: nothing is destroyed and new activity brings the
   *  row back. Pass false to unhide. */
  async setHidden(conversationId: string, hidden: boolean): Promise<void> {
    await http.put(`${base}/${conversationId}/hidden`, { hidden });
  },

  /** Advance the caller's read cursor (monotonic — never moves back).
   *  Server-side this also records the receipt mark and emits the read event. */
  async advanceReadCursor(conversationId: string, seq: number): Promise<void> {
    await http.put(`${base}/${conversationId}/read-cursor`, { seq });
  },

  /** Acknowledge that this client actually RECEIVED messages up to seq.
   *  Separate from read on purpose: delivery is not gated on the tab being
   *  focused, which is the whole difference between two grey ticks and two
   *  blue ones. */
  async acknowledgeDelivery(conversationId: string, seq: number): Promise<void> {
    await http.put(`${base}/${conversationId}/delivered-cursor`, { seq });
  },

  /** My own read-receipt setting. */
  async readReceiptPref(): Promise<boolean> {
    const { data } = await http.get<{ read_receipts: boolean }>(`${base}/preferences`);
    return data.read_receipts;
  },

  /** Set my own read-receipt setting. Reciprocal: off means I share no reads
   *  AND see nobody else's. */
  async setReadReceiptPref(on: boolean): Promise<void> {
    await http.put(`${base}/preferences`, { read_receipts: on });
  },

  /** Message Info for ONE of my messages: who has received it and who has
   *  opened it, with the server's own timestamps. Sender-only server-side. */
  async messageReceipts(conversationId: string, messageId: string): Promise<MessageReceipts> {
    const { data } = await http.get<MessageReceipts>(
      `${base}/${conversationId}/messages/${messageId}/receipts`,
    );
    return data;
  },

  /** Aggregate receipt cursors for the conversation — one call renders ticks
   *  for an entire screen of messages, so there is no per-message query. */
  async receipts(conversationId: string): Promise<ConversationReceipts> {
    const { data } = await http.get<ConversationReceipts>(`${base}/${conversationId}/receipts`);
    return data;
  },

  // ── Spaces (department / team / company) ──────────────────────────────────
  async createSpace(kind: SpaceKind, opts: { contextId?: string; title?: string } = {}): Promise<Conversation> {
    const { data } = await http.post<Conversation>(`${base}/spaces`, {
      kind,
      context_id: opts.contextId,
      title: opts.title,
    });
    return data;
  },

  async resyncSpace(conversationId: string): Promise<void> {
    await http.post(`${base}/${conversationId}/resync`);
  },

  // ── Circles (hand-picked groups) ──────────────────────────────────────────
  /** Create a hand-picked group ("Circle") with the chosen members; caller = OWNER. */
  async createGroup(title: string, memberUserIds: string[]): Promise<Conversation> {
    const { data } = await http.post<Conversation>(`${base}/groups`, { title, member_user_ids: memberUserIds });
    return data;
  },

  /** Rename a group (OWNER/ADMIN). */
  async updateGroup(conversationId: string, title: string): Promise<void> {
    await http.patch(`${base}/${conversationId}`, { title });
  },

  /** Details edit (OWNER/ADMIN): only provided fields change; '' clears. */
  async updateDetails(
    conversationId: string,
    patch: { title?: string; description?: string; avatar_file_ref?: string; posting_policy?: 'ALL_MEMBERS' | 'ADMINS_ONLY' | 'OWNERS_ONLY' },
  ): Promise<void> {
    await http.patch(`${base}/${conversationId}`, patch);
  },

  /** Members with roles (participants only). */
  async getMembers(conversationId: string): Promise<import('../types').ConversationMember[]> {
    const { data } = await http.get(`${base}/${conversationId}/members`);
    return Array.isArray(data?.members) ? data.members : [];
  },

  /** OWNER: promote to ADMIN / demote to MEMBER. */
  async setMemberRole(conversationId: string, userId: string, role: 'ADMIN' | 'MEMBER'): Promise<void> {
    await http.put(`${base}/${conversationId}/members/${encodeURIComponent(userId)}/role`, { role });
  },

  /** OWNER: hand ownership to another member (you stay ADMIN). */
  async transferOwnership(conversationId: string, userId: string): Promise<void> {
    await http.post(`${base}/${conversationId}/transfer-ownership`, { user_id: userId });
  },

  /** OWNER: archive for everyone (history kept). */
  async archive(conversationId: string): Promise<void> {
    await http.post(`${base}/${conversationId}/archive`, {});
  },

  /** Leave a group (an owner must transfer ownership first). */
  async leave(conversationId: string): Promise<void> {
    await http.post(`${base}/${conversationId}/leave`, {});
  },

  /** My notification level for this conversation (existing per-member prefs). */
  async setNotifications(conversationId: string, level: 'ALL' | 'MENTIONS' | 'NONE', mutedUntil?: string | null): Promise<void> {
    await http.put(`${base}/${conversationId}/notifications`, { notification_level: level, muted_until: mutedUntil ?? null });
  },

  /** Add people to a group (OWNER/ADMIN). */
  async addMembers(conversationId: string, userIds: string[]): Promise<void> {
    await http.post(`${base}/${conversationId}/members`, { user_ids: userIds });
  },

  /** Remove a person from a group (OWNER/ADMIN). */
  async removeMember(conversationId: string, userId: string): Promise<void> {
    await http.delete(`${base}/${conversationId}/members/${encodeURIComponent(userId)}`);
  },

  // ── Typing + presence (ephemeral) ─────────────────────────────────────────
  /** Heartbeat: I'm typing in this conversation. */
  async setTyping(conversationId: string): Promise<void> {
    await http.post(`${base}/${conversationId}/typing`, {});
  },

  /** Who's typing here right now (user ids, excluding me). */
  async getTyping(conversationId: string): Promise<string[]> {
    const { data } = await http.get<{ user_ids: string[] }>(`${base}/${conversationId}/typing`);
    return data.user_ids ?? [];
  },

  /** Online status for a set of users. */
  async getAvailability(userIds: string[]): Promise<Availability[]> {
    if (userIds.length === 0) return [];
    const { data } = await http.get<{ items: Availability[] }>(`${base}/availability`, {
      params: { user_ids: userIds.join(',') },
    });
    return data.items ?? [];
  },

  /** My status + the statuses I may pick + the server's explanatory note. */
  async getMyAvailability(): Promise<MyAvailability> {
    const { data } = await http.get<MyAvailability>(`${base}/availability/me`);
    return { availability: data.availability, options: data.options ?? [], note: data.note ?? '' };
  },

  /** Set my status. `minutes` 0 means "the rest of today"; the server expires it. */
  async setMyAvailability(input: AvailabilityInput): Promise<Availability> {
    const { data } = await http.put<{ availability: Availability }>(`${base}/availability/me`, input);
    return data.availability;
  },

  /** Drop my manual status — what the server derives takes over again. */
  async clearMyAvailability(): Promise<Availability> {
    const { data } = await http.delete<{ availability: Availability }>(`${base}/availability/me`);
    return data.availability;
  },

  // ── Contextual conversations (attached to a domain object) ────────────────
  /** Resolve-or-create the discussion attached to an object (idempotent per
   *  context_type+context_id); the caller joins. Backs the "Discuss" affordance. */
  async createContextual(contextType: string, contextId: string, title?: string): Promise<Conversation> {
    const { data } = await http.post<Conversation>(`${base}/contextual`, {
      context_type: contextType,
      context_id: contextId,
      title,
    });
    return data;
  },

  // ── Reactions ─────────────────────────────────────────────────────────────
  async getReactions(conversationId: string, messageId: string): Promise<ReactionResult> {
    const { data } = await http.get<ReactionResult>(`${base}/${conversationId}/messages/${messageId}/reactions`);
    return data;
  },

  async addReaction(conversationId: string, messageId: string, key: string): Promise<ReactionResult> {
    const { data } = await http.put<ReactionResult>(
      `${base}/${conversationId}/messages/${messageId}/reactions/${encodeURIComponent(key)}`,
    );
    return data;
  },

  async removeReaction(conversationId: string, messageId: string, key: string): Promise<ReactionResult> {
    const { data } = await http.delete<ReactionResult>(
      `${base}/${conversationId}/messages/${messageId}/reactions/${encodeURIComponent(key)}`,
    );
    return data;
  },

  // ── Dana ──────────────────────────────────────────────────────────────────
  /** Ask Dana in a conversation (posts the question + Dana's reply). Proposals in
   *  the result are confirm-first — never auto-executed. */
  async askDana(conversationId: string, message: string): Promise<DanaResult> {
    const { data } = await http.post<DanaResult>(`${base}/${conversationId}/dana`, { message });
    return data;
  },
};

export type ConversationsApi = typeof conversationsApi;
